import { useCallback, useEffect, useState } from "react";

import { DEFAULT_LOCALE, TRANSLATION_SETTINGS_STORAGE_KEY } from "../constants";
import type { SupportedLocale } from "../types";
import type { TranslationSettings } from "./translationSettingsSchema";
import {
  readTranslationSettings,
  TRANSLATION_SETTINGS_CHANGED_EVENT,
  writeTranslationSettings,
} from "./translationSettingsStorage";

interface UseTranslationSettingsResult {
  settings: TranslationSettings;
  setLocale: (locale: SupportedLocale) => void;
}

export function useTranslationSettings(): UseTranslationSettingsResult {
  const [settings, setSettings] = useState(readTranslationSettings);

  useEffect(() => {
    function handleTranslationSettingsChanged(event: Event) {
      setSettings((event as Event & { detail: TranslationSettings }).detail);
    }

    function handleStorageChanged(event: StorageEvent) {
      if (event.key === TRANSLATION_SETTINGS_STORAGE_KEY) {
        setSettings(readTranslationSettings());
      }
    }

    window.addEventListener(TRANSLATION_SETTINGS_CHANGED_EVENT, handleTranslationSettingsChanged);
    window.addEventListener("storage", handleStorageChanged);

    return () => {
      window.removeEventListener(
        TRANSLATION_SETTINGS_CHANGED_EVENT,
        handleTranslationSettingsChanged
      );
      window.removeEventListener("storage", handleStorageChanged);
    };
  }, []);

  const setLocale = useCallback((locale: SupportedLocale) => {
    const currentSettings = readTranslationSettings();
    if (currentSettings.locale === locale) {
      return;
    }

    writeTranslationSettings({
      ...currentSettings,
      enabled: locale !== DEFAULT_LOCALE,
      locale,
      updatedAt: new Date().toISOString(),
    });
  }, []);

  return { settings, setLocale };
}
